// src/pages/admin/contact/ContactList.js
import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;

export default function ContactList() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q) return rows;
    const key = q.toLowerCase();
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(key) ||
        r.email?.toLowerCase().includes(key) ||
        r.phone?.toLowerCase().includes(key)
    );
  }, [rows, q]);

  const fetchPage = async (p = 0) => {
    setLoading(true);
    try {
      const res = await api.get("/api/contact/inquiries", {
        params: { page: p, size: PAGE_SIZE },
      });
      const data = res.data?.data;

      if (data?.content) {
        setRows(data.content);
        setTotal(data.totalElements ?? data.content.length);
      } else if (Array.isArray(data)) {
        setRows(data.slice(p * PAGE_SIZE, (p + 1) * PAGE_SIZE));
        setTotal(data.length);
      } else {
        setRows([]);
        setTotal(0);
      }
      setPage(p);
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fmtDateTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    const ss = String(dt.getSeconds()).padStart(2, "0");
    return `${yyyy}. ${mm}. ${dd}. ${hh}:${mi}:${ss}`;
  };

  return (
    <AdminLayout>
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
          온라인지원 관리
        </h1>
      </div>

      {/* 상단 툴바 */}
      <div className="mb-3 flex flex-col justify-between gap-3 md:flex-row md:items-center">
        <div className="text-sm text-slate-600">
          총 <b>{total}</b> 건
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="이름/이메일/전화 검색"
            className="w-56 rounded-xl border px-3 py-2 text-sm outline-none focus:border-blue-300"
          />
          <button
            onClick={() => fetchPage(0)}
            className="rounded-xl border bg-white px-3 py-2 text-sm hover:bg-slate-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 표 */}
      <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
        <table className="min-w-[720px] w-full table-fixed">
          <colgroup>
            <col className="w-16" />
            <col />
            <col className="w-36" />
            <col className="w-44" />
            <col className="w-44" />
            <col className="w-28" />
          </colgroup>
          <thead className="bg-slate-50 text-sm">
            <tr className="text-slate-700">
              <th className="px-4 py-3 text-left whitespace-nowrap">#</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">
                {/* 모바일에서는 짧게 "제목"만, md 이상에서 전체 문구 */}
                <span className="inline md:hidden">제목</span>
                <span className="hidden md:inline">
                  제목(이름 / 서비스 / 직무)
                </span>
              </th>
              <th className="px-4 py-3 text-left whitespace-nowrap">이메일</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">
                전화번호
              </th>
              <th className="px-4 py-3 text-left whitespace-nowrap">작성일</th>
              <th className="px-4 py-3 text-left whitespace-nowrap">작업</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-slate-500"
                  colSpan={6}
                >
                  불러오는 중...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-slate-500"
                  colSpan={6}
                >
                  데이터가 없습니다.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((r, i) => (
                <tr key={r.id ?? i} className="border-t hover:bg-slate-50/60">
                  <td className="px-4 py-3 whitespace-nowrap">
                    {page * PAGE_SIZE + i + 1}
                  </td>
                  <td className="truncate px-4 py-3">
                    <div className="font-medium text-slate-800">
                      {r.name ?? "-"}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {r.service ?? r.service?.name ?? "-"} ·{" "}
                      {r.job ?? r.job?.name ?? "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.email}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.phone}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {fmtDateTime(r.createdAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-2">
                      <Link
                        to={`/admin/contact/${r.id}`}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                      >
                        보기
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="mt-5 flex justify-center">
        <Pagination
          page={page}
          pageCount={pageCount}
          onChange={(p) => fetchPage(p)}
        />
      </div>
    </AdminLayout>
  );
}

/* ====== 간단 페이지네이션 컴포넌트 ====== */
function Pagination({ page, pageCount, onChange }) {
  const go = (p) => {
    const np = Math.min(Math.max(0, p), pageCount - 1);
    if (np !== page) onChange(np);
  };
  return (
    <div className="inline-flex items-center overflow-hidden rounded-lg border bg-white">
      <button
        className="px-3 py-2 text-slate-600 hover:bg-slate-50"
        onClick={() => go(0)}
      >
        «
      </button>
      <button
        className="px-3 py-2 text-slate-600 hover:bg-slate-50"
        onClick={() => go(page - 1)}
      >
        ‹
      </button>
      <span className="px-3 py-2 font-semibold text-white bg-blue-600">
        {page + 1}
      </span>
      <button
        className="px-3 py-2 text-slate-600 hover:bg-slate-50"
        onClick={() => go(page + 1)}
      >
        ›
      </button>
      <button
        className="px-3 py-2 text-slate-600 hover:bg-slate-50"
        onClick={() => go(pageCount - 1)}
      >
        »
      </button>
    </div>
  );
}
